/**
 * BedrockAgentCoreClient - Handles core Bedrock Agent invocations
 */

import HelperFunctions from "../../mango/js/common/HelperFunctions";

// Static constants
const REGION = "us-west-2";
const QUALIFIER = "DEFAULT";
const CANCELLATION_ERROR_MESSAGE = "Request stopped by user.";
const AUTH_EXPIRATION_ERROR_MESSAGE = "Your authentication is expiring."
    + " Please reload the page to refresh your session.";
const AUTH_EXPIRATION_ERROR_CODE = -32001;

class BedrockAgentCoreClient {
    constructor(auth) {
        this.auth = auth;
        this.currentAbortController = null;

        // Get dynamic configuration based on environment
        this.config = this.getEnvironmentConfig();
    }

    /**
     * Get dynamic configuration based on environment
     * @returns {Object} Configuration object with agentRuntimeArn
     */
    getEnvironmentConfig() {
        if (HelperFunctions.isProd()) {
            return {
                agentRuntimeArn: "arn:aws:bedrock-agentcore:us-west-2:608713827895:runtime/prod_website-53jfsrEjih"
            };
        }
        // Default to beta configuration (includes isBeta() and development)
        return {
            agentRuntimeArn: "arn:aws:bedrock-agentcore:us-west-2:280033535693:runtime/beta_website-vtFdQEGg4m"
        };
    }

    /**
     * Invokes the Bedrock Agent with streaming response
     * @param {string} prompt - The user's input prompt
     * @param {function} onChunk - Callback for each streamed chunk
     * @param {string} sessionId - session ID
     * @returns {Promise<void>}
     */
    async invokeAgentStream(prompt, onChunk, sessionId) {
        if (!prompt || !prompt.trim()) {
            throw new Error("Prompt is required");
        }

        // Prepare the request payload
        const requestPayload = {
            input: {
                prompt: prompt.trim()
            },
            context: {
                user: this.auth.userId
            }
        };

        // Only include session_id if sessionId is truthy
        if (sessionId) {
            requestPayload.context.session_id = sessionId;
        }

        const encodedAgentRuntimeArn = encodeURIComponent(this.config.agentRuntimeArn);
        const url = `https://bedrock-agentcore.${REGION}.amazonaws.com/runtimes/${encodedAgentRuntimeArn}/invocations?qualifier=${QUALIFIER}`;


        // Get the access token from auth
        const accessJwtToken = this.auth.getSignInUserSession().getAccessToken().jwtToken;

        // Generate unique runtime session ID (33 random digits) and trace ID
        const runtimeSessionId = Array.from({ length: 33 }, () => Math.floor(Math.random() * 10)).join("");
        const traceId = `trace-${Date.now()}`;

        console.log("Invoking AgentCore with streaming:", {
            url, agentRuntimeArn: this.config.agentRuntimeArn, region: REGION, sessionId, runtimeSessionId
        });

        // Create AbortController for this request
        this.currentAbortController = new AbortController();

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessJwtToken}`,
                    "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": runtimeSessionId,
                    "X-Amzn-Trace-Id": traceId
                },
                body: JSON.stringify(requestPayload),
                signal: this.currentAbortController.signal
            });

            console.log("response from agent: ", response);

            if (!response.ok) {
                // Handle 401 authentication errors with specific JSON-RPC error parsing
                if (response.status === 401) {
                    try {
                        const errorResponse = await response.json();

                        // Check for the specific JSON-RPC error format and code
                        if (errorResponse.error &&
                            errorResponse.error.code === AUTH_EXPIRATION_ERROR_CODE &&
                            errorResponse.error.message &&
                            errorResponse.error.message.includes("expire")) {
                            console.warn("Authentication token expiring:", errorResponse.error.message);
                            throw new Error(AUTH_EXPIRATION_ERROR_MESSAGE);
                        }
                    } catch (parseError) {
                        // If we can't parse the response, fall back to generic 401 handling
                        console.warn("Failed to parse 401 error response:", parseError);
                    }
                }

                throw new Error(`Agent invocation failed with status: ${response.status}`);
            }

            // Get the readable stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            try {
                while (true) {
                    // eslint-disable-next-line no-await-in-loop
                    const { done, value } = await reader.read();

                    if (done) break;

                    // Decode the chunk and add to buffer
                    buffer += decoder.decode(value, { stream: true });

                    // Process complete lines (NDJSON format)
                    const lines = buffer.split("\n");
                    buffer = lines.pop(); // Keep incomplete line in buffer

                    // eslint-disable-next-line no-restricted-syntax
                    for (const line of lines) {
                        if (line.trim()) {
                            try {
                                onChunk(JSON.parse(line));
                            } catch (e) {
                                console.warn("Failed to parse chunk:", line);
                            }
                        }
                    }
                }
            } finally {
                reader.releaseLock();
            }
        } catch (error) {
            // Check if the error is due to user cancellation
            if (error.name === "AbortError") {
                console.log("Bedrock Agent request was cancelled by user");
                throw new Error(CANCELLATION_ERROR_MESSAGE);
            }
            console.error("Error invoking Bedrock Agent with streaming:", error);
            throw error;
        } finally {
            // Clean up the AbortController
            this.currentAbortController = null;
        }
    }

    /**
     * Cancels the current streaming request if one is in progress
     */
    cancelCurrentRequest() {
        if (this.currentAbortController) {
            this.currentAbortController.abort();
        }
    }
}

export { BedrockAgentCoreClient, CANCELLATION_ERROR_MESSAGE, AUTH_EXPIRATION_ERROR_MESSAGE };
